<template>
    <p id="healthQuestion" class="mt-2">
        If you are happy to answer the health question below, we will adjust your life expectancy to allow for your health. If not, we will use average life expectancy.
    </p>
    <div class="radio-health-options my-4">
        <template v-for="(healthOption, index) in healthOptions" :key="index">
            <div class="my-3">
                <label :for="`radio-health-${index}`">
                    <input class="form-check-input"
                           type="radio"
                           :id="`radio-health-${index}`"
                           :value="healthOption.healthState"
                           :checked="healthOption.healthState == selectedHealthState"
                           name="healthStatus"
                           :disabled="disableInputs"
                           @change="changeHealthStatus(healthOption.healthState)" />
                    <span class="custom-radio"></span>
                    {{ healthOption.text }}
                    <help :title="getHelpTitle(healthOption.healthState)"
                          :text="getHelpText(healthOption.healthState)"
                          :isWide="true" />
                </label>
            </div>
        </template>
    </div>

    <template v-if="displayLifeExpectancyText">
        <p class="mb-3">
            Based on your answer above, you may be expected to live on average to around <span class="fw-bold">{{ ageAtDeath }}</span>.
        </p>
        <p class="mb-4">
            This means you need a plan which pays you an income for at least <span class="fw-bold">{{ lifeExpectancy }}</span> years, if you retire when planned.
        </p>
    </template>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, watch } from "vue";
    import HelpText from "@/guiide/ts/helpText";
    import Help from "@/guiide/components/common/Help.vue";
    import HealthState from "@/guiide/ts/healthState";
    import { useStore } from "vuex";
    import { app } from "@/guiide/ts/app";
    import { getAgeAtRetirement } from "@/guiide/ts/utilities";

    const store = useStore();
    const selectedHealthState = ref(store.state.retiree?.health);
    const disableInputs = ref(false);

    const emit = defineEmits(['complete']);

    onMounted(async () => {
        if (store.state.retiree?.health != null) {
            emit("complete");
        }
    });

    watch(
        () => store.state.retiree,
        (newRetireeValue) => {
            selectedHealthState.value = newRetireeValue?.health;

            if (newRetireeValue?.health != null) {
                emit("complete");
            }
        },
        { immediate: true }
    );

    const displayLifeExpectancyText = computed<boolean>(() => store.state.retiree?.lifeExpectancy != null && store.state.retiree?.lifeExpectancy != 0);

    const lifeExpectancy = computed<number>(() => store.state.retiree?.lifeExpectancy ?? 0);

    const ageAtDeath = computed<number>(() => {
        const ageAtRetirement = getAgeAtRetirement();
        return ageAtRetirement == null ? 0 : ageAtRetirement + lifeExpectancy.value;
    });

    function getHelpTitle(healthState: HealthState) {
        switch (healthState) {
            case HealthState.PreferNotToSay:
                return "Rather not say";
            case HealthState.Perfect:
                return "Perfect health";
            case HealthState.Minor:
                return "Minor health issues";
            case HealthState.Moderate:
                return "Moderate health issues";
            case HealthState.Major:
                return "Major health issues";
        }
    }

    function getHelpText(healthState: HealthState) {
        switch (healthState) {
            case HealthState.PreferNotToSay:
                return HelpText.healthStateRatherNotSay();
            case HealthState.Perfect:
                return HelpText.healthStatePerfect();
            case HealthState.Minor:
                return HelpText.healthStateMinor();
            case HealthState.Moderate:
                return HelpText.healthStateModerate();
            case HealthState.Major:
                return HelpText.healthStateMajor();
        }
    }

    async function changeHealthStatus(healthState: HealthState) {
        disableInputs.value = true;
        selectedHealthState.value = healthState;
        await app.updateHealthState(healthState);
        disableInputs.value = false;
    }

    function createHealthOption(healthState: HealthState, text: string) {
        return { healthState, text };
    }

    const healthOptions = [
        createHealthOption(HealthState.PreferNotToSay, "I'd rather not say"),
        createHealthOption(HealthState.Perfect, "I am in perfect health"),
        createHealthOption(HealthState.Minor, "I have some minor health issues"),
        createHealthOption(HealthState.Moderate, "I have some moderate health issues"),
        createHealthOption(HealthState.Major, "I have some major health issues"),
    ];
</script>

<style scoped lang="css">
    .form-check-input {
        width: 0;
        height: 0;
        opacity: 0;
        vertical-align: middle;
    }

    .radio-health-options label span {
        background: url("/Content/images/radio.png");
        width: 27px;
        height: 27px;
        display: inline-block;
        pointer-events: none;
        vertical-align: middle;
        margin-right: 2px;
        margin-top: -1px;
    }

    input[type="radio"]:checked + .custom-radio {
        background-position: -27px;
        transition: background-position 0.5s steps(1, start);
    }
</style>
